/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/require-default-props */
import { Menu } from '@szhsin/react-menu';
import type { ComponentProps, FC } from 'react';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { Range } from '@catalyze/ui';
import type { Filters } from 'constants/filters';
import type {
    TAvailableRoutes,
    TFilterState,
} from 'features/RealEstate/context/FilterContext';
import {
    availableRoutes,
    useFilterContext,
    updateFiltersAction,
} from 'features/RealEstate/context/FilterContext';

import { CheckboxMenu } from './components/CheckboxMenu';
import { CheckboxMenuTags } from './components/CheckboxMenu/components/CheckboxMenuTags';
import { DateRangeMenu } from './components/DateRangeMenu';
import { DateRangeMenuTags } from './components/DateRangeMenu/components/DateRangeMenuTags';
import { Dropdown } from './components/Dropdown';
import { RangeMenu } from './components/RangeMenu';
import { RangeMenuTags } from './components/RangeMenu/components/RangeMenuTags';
import { FlagLabels, Flags } from './constants/flags';
import {
    Container,
    Divider,
    DropdownContainer,
    Search,
    StyledCheckboxRow,
    TagContainer,
} from './styled';
import { formatFiltersFromState } from './utils/filters';
import {
    edmRangeTextTransformFunctor,
    priceRangeTextTransformFunctor,
} from './utils/tagTextTransforms';
import { useCurrentRoute } from './utils/useCurrentRoute';

interface IRangeFormData {
    [Range.MIN]: string;
    [Range.MAX]: string;
}

export interface IFilters {
    states?: string[];
    cities?: string[];
    leadTypes?: Array<string | null>;
    marketValue?: { from: number; to: number };
    edms?: { from: number; to: number };
    createdDates?: { fromDate: string; toDate: string };
}

export interface IFormData {
    cities: string[];
    states: string[];
    leadTypes: string[];
    flags: string[];
    edmCount: IRangeFormData;
    added: IRangeFormData;
    estimatedMarketValue: IRangeFormData;
}

type IFilterProps = ComponentProps<typeof Container> & {
    enabledFeatures?: string[];
    filters?: IFilters;
    onSearchHandler?: (value: string) => void;
    onFiltersChange?: (filters: Record<Filters, TFilterState>) => void;
};

export const Filter: FC<IFilterProps> = ({
    enabledFeatures,
    filters = {},
    onFiltersChange,
    ...props
}) => {
    const { filterState, filterDispatch } = useFilterContext();
    const currentRoute = useCurrentRoute();
    const globalFilterData = filterState[currentRoute as TAvailableRoutes];
    onFiltersChange?.(globalFilterData);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const globalFilterData = filterState[currentRoute as TAvailableRoutes];
        onFiltersChange?.(globalFilterData);
    }, [filterState, currentRoute]);

    // TODO: based on data
    const [ranges, setRanges] = useState({
        minPrice: 0,
        maxPrice: 0,
        minDate: '',
        maxDate: '',
        minEdm: 0,
        maxEdm: 0,
    });

    const onSearch = useCallback((value: string) => {
        if (props.onSearchHandler) {
            props.onSearchHandler(value);
        }
    }, []);

    const formattedCities = useMemo(() => {
        if (filters?.cities) {
            return filters?.cities?.map((city) => ({
                value: city,
                label: city,
            }));
        }

        return [];
    }, [filters?.cities]);

    const formattedStates = useMemo(() => {
        if (filters?.states) {
            return filters?.states.map((city) => ({
                value: city,
                label: city,
            }));
        }

        return [];
    }, [filters?.states]);

    const formattedLeadTypes = useMemo(() => {
        if (filters?.leadTypes) {
            return filters.leadTypes.map((leadType) => ({
                value: leadType,
                label: leadType ?? '(none)',
            }));
        }
        return [];
    }, [filters?.leadTypes]);

    const formMethods = useForm<any>({
        defaultValues: {
            cities: globalFilterData.cities || [],
            states: globalFilterData.states || [],
            leadTypes: globalFilterData.leadTypes || [],
            flags: [],
            edmCount: {
                [Range.MIN]:
                    globalFilterData.edmMin || ranges.minEdm.toString(),
                [Range.MAX]:
                    globalFilterData.edmMax || ranges.maxEdm.toString(),
            },
            added: {
                [Range.MIN]: globalFilterData.fromDate || undefined,
                [Range.MAX]: globalFilterData.toDate || undefined,
            },
            estimatedMarketValue: {
                [Range.MIN]:
                    globalFilterData.priceMin || ranges.minPrice.toString(),
                [Range.MAX]:
                    globalFilterData.priceMax || ranges.maxPrice.toString(),
            },
        },
    });

    useDeepCompareEffect(() => {
        setRanges({
            minPrice: filters?.marketValue?.from ?? 0,
            maxPrice: filters?.marketValue?.to ?? 0,
            minEdm: filters?.edms?.from ?? 0,
            maxEdm: filters?.edms?.to ?? 0,
            minDate: filters?.createdDates?.fromDate ?? '',
            maxDate: filters?.createdDates?.toDate ?? '',
        });
    }, [filters]);

    const { watch } = formMethods;

    const values = watch();

    useDeepCompareEffect(() => {
        if (onFiltersChange) {
            if (availableRoutes.includes(currentRoute as TAvailableRoutes)) {
                filterDispatch(
                    updateFiltersAction(
                        currentRoute as TAvailableRoutes,
                        formatFiltersFromState(values)
                    )
                );
            }
            onFiltersChange(values);
        }
    }, [values]);
    const flags = watch('flags');

    return (
        <FormProvider {...formMethods}>
            <Container {...props}>
                <Search disableShrinking onChange={onSearch} />
                <DropdownContainer>
                    <Menu menuButton={<Dropdown label="City" />}>
                        <CheckboxMenu
                            options={formattedCities}
                            inputName="cities"
                        />
                    </Menu>
                    <Menu menuButton={<Dropdown label="State" />}>
                        <CheckboxMenu
                            options={formattedStates}
                            inputName="states"
                        />
                    </Menu>
                    <Menu menuButton={<Dropdown label="EDM" />}>
                        {enabledFeatures?.includes(
                            Flags.LEADS_WITHOUT_CONTACTED
                        ) && (
                            <>
                                <StyledCheckboxRow
                                    label="Show leads without Contacted EDMs"
                                    value={Flags.LEADS_WITHOUT_CONTACTED}
                                    inputName="flags"
                                />
                                <Divider />
                            </>
                        )}

                        <RangeMenu
                            inputName="edmCount"
                            lowerBoundText="Min no. of EDMs"
                            upperBoundText="Max no. of EDMs"
                            min={ranges.minEdm}
                            max={ranges.maxEdm}
                            disabled={flags.includes(
                                Flags.LEADS_WITHOUT_CONTACTED
                            )}
                        />
                    </Menu>
                    <Menu menuButton={<Dropdown label="Scenario" />}>
                        <CheckboxMenu
                            options={formattedLeadTypes}
                            inputName="leadTypes"
                        />
                    </Menu>
                    <Menu menuButton={<Dropdown label="Date Added" />}>
                        <DateRangeMenu
                            inputName="added"
                            lowerBoundText="From"
                            upperBoundText="To"
                        />
                    </Menu>
                    <Menu
                        menuButton={<Dropdown label="Estimated Market Value" />}
                    >
                        <RangeMenu
                            inputName="estimatedMarketValue"
                            lowerBoundText="Min est. market value"
                            upperBoundText="Max est. market value"
                            min={ranges.minPrice}
                            max={ranges.maxPrice}
                        />
                    </Menu>
                </DropdownContainer>
                <TagContainer>
                    <CheckboxMenuTags map={FlagLabels} inputName="flags" />
                    <CheckboxMenuTags
                        map={formattedCities}
                        inputName="cities"
                    />
                    <CheckboxMenuTags
                        map={formattedStates}
                        inputName="states"
                    />
                    <CheckboxMenuTags
                        map={formattedLeadTypes}
                        inputName="leadTypes"
                    />
                    {!flags.includes(Flags.LEADS_WITHOUT_CONTACTED) && (
                        <RangeMenuTags
                            inputName="edmCount"
                            textTransform={edmRangeTextTransformFunctor(
                                ranges.minEdm,
                                ranges.maxEdm
                            )}
                            show={values.edmCount.max > 0}
                        />
                    )}
                    <DateRangeMenuTags inputName="added" />
                    <RangeMenuTags
                        inputName="estimatedMarketValue"
                        textTransform={priceRangeTextTransformFunctor(
                            ranges.minPrice,
                            ranges.maxPrice
                        )}
                        show={values.estimatedMarketValue.max > 0}
                    />
                </TagContainer>
            </Container>
        </FormProvider>
    );
};

Filter.defaultProps = {
    enabledFeatures: [],
};
